import type { Technique, TechniqueCategory, TechniqueContext } from './types';

/**
 * Technique registry.
 *
 * Registration is the *only* coupling point between a technique and the rest of the
 * app: everything else — controls, randomisation, batch ranges, the layer picker — is
 * derived from the `Technique` object itself.
 *
 * A technique's state type is private to that technique, so the registry stores
 * `Technique<unknown>`. `erase()` is the single place where a state is cast back to
 * its concrete type; that is sound because the host only ever hands a state to the
 * same technique that produced it, and keeping the cast here means no `any` leaks
 * into technique code.
 */
export type RegisteredTechnique = Technique<unknown>;

function erase<S>(t: Technique<S>): RegisteredTechnique {
  return {
    ...t,
    init: (ctx: TechniqueContext) => t.init(ctx) as unknown,
    step: (state: unknown, dt: number, ctx: TechniqueContext) => t.step(state as S, dt, ctx) as unknown,
    isComplete: (state: unknown) => t.isComplete(state as S),
    draw: (state, target, ctx) => t.draw(state as S, target, ctx),
    toSVG: t.toSVG ? (state, ctx) => (t.toSVG as (s: S, c: TechniqueContext) => string)(state as S, ctx) : undefined,
    elementCount: t.elementCount
      ? (state) => (t.elementCount as (s: S) => number)(state as S)
      : undefined,
  };
}

const registry = new Map<string, RegisteredTechnique>();

export function registerTechnique<S>(technique: Technique<S>): void {
  if (registry.has(technique.id)) {
    throw new Error(`Technik-ID doppelt vergeben: ${technique.id}`);
  }
  registry.set(technique.id, erase(technique));
}

export function getTechnique(id: string): RegisteredTechnique | undefined {
  return registry.get(id);
}

export function listTechniques(): RegisteredTechnique[] {
  return [...registry.values()];
}

export function techniquesByCategory(): { category: TechniqueCategory; techniques: RegisteredTechnique[] }[] {
  const groups = new Map<TechniqueCategory, RegisteredTechnique[]>();
  for (const t of registry.values()) {
    const list = groups.get(t.category);
    if (list) list.push(t);
    else groups.set(t.category, [t]);
  }
  return [...groups.entries()].map(([category, techniques]) => ({ category, techniques }));
}
